import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import { styled, SwitchProps, Theme } from "@mui/material";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import Icon from "@mui/material/Icon";
import { Resource } from "permissions/entities/permission/resources/general";
import { PermissionToggle } from "permissions/entities/permission/view/components/permissions-table/components/permission-toggle";
import {
  Center,
  GroupedPermissionsContainer,
  PermissionCardHeader,
  PermissionTitleContainer,
  SmallSelect
} from "permissions/entities/permission/view/components/permissions-table/styles";
import {
  CardMeta,
  CardType,
  CategoryLabel,
  ControlMeta,
  TableRow,
  UniversalCellConfig
} from "permissions/entities/permission/view/components/permissions-table/types";
import {
  getCardStyle,
  getResourceBaseColor,
  toTemplateCardMeta
} from "permissions/entities/permission/view/components/permissions-table/utils";
import { JSX } from "react";

const PackedCard = styled(Card)<{
  cols: number;
  rows: number;
}>(({
  cols,
  rows
}) => ({
  gridColumn: `span ${cols}`,
  gridRow: `span ${rows}`,
  display: 'flex',
  flexDirection: 'column',
  gridTemplateRows: 'min-content minmax(0, 1fr)',
  height: '100%',
  overflowY: 'auto'
}));

const GroupedCardActions = styled(CardActions)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBlock: 'auto',
  padding: theme.spacing(1),
  paddingTop: 0
}));

export type GroupedTemplatePermissionCardProps = {
  row: TableRow;
  config: Extract<UniversalCellConfig, { cardType: CardType.Grouped }>;
  theme: Theme;
  activeCategory: CategoryLabel | "";
  getCardSubheader(resource: Resource): string | undefined;
  canEdit?: boolean;
  onToggle(resource: Resource): void;
  onApplyMeta(
    resource: Resource,
    meta: ControlMeta,
    newValue: string
  ): void;
};

export default function GroupedTemplatePermissionCard(
  props: Readonly<GroupedTemplatePermissionCardProps>): JSX.Element | null {
  const {
    row,
    config,
    theme,
    activeCategory,
    getCardSubheader,
    canEdit,
    onToggle,
    onApplyMeta
  } = props;

  const items = config.keys.map((key) => {
    const fromBuffer = config.candidatesForKey?.(row, key)?.[0] as Resource | undefined;
    const resource = fromBuffer ?? config.templateForKey?.(key);
    if (!resource) return;
    const control = config.controlMetaForResource(resource, key);
    return toTemplateCardMeta(resource, key, control);
  })?.filter(Boolean) as CardMeta[];

  if (!items.length) return <Icon>remove</Icon>;

  const resource = items[0].resource;
  const cardTitle = config.title;
  const cardSubHeader = getCardSubheader(resource);
  const colSpan = config.colSpan ?? 2;
  const rowSpan = config.rowSpan ?? 2;

  const isDisabled: boolean = items.every((i) => i.resource.isDisabled);

  const { bgColor, borderColor, display, textColor, selectSx } =
    getCardStyle(theme, resource, isDisabled, activeCategory);

  return (
    <PackedCard
      key={config.title ?? "group"}
      cols={colSpan}
      rows={rowSpan}
      sx={{ backgroundColor: bgColor, borderColor, color: textColor, display }}
      variant="outlined"
    >
      {(config.title || cardSubHeader) && (
        <PermissionCardHeader
          grouped={true}
          title={<PermissionTitleContainer grouped={true}>{cardTitle}</PermissionTitleContainer>}
          subheader={
            <>
              {cardSubHeader !== cardTitle && <span title={cardSubHeader}>{cardSubHeader}</span>}
            </>
          }
          slotProps={{
            subheader: {
              sx: {
                maxWidth: "150px",
                mt: 1,
                overflow: "hidden",
                textAlign: "left",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              },
            },
          }}
        />
      )}

      <Center>
        <GroupedCardActions>
          <GroupedPermissionsContainer
            sx={canEdit ? {
              gap: 1,
              gridTemplateColumns: `auto 1fr ${colSpan > 1 ? "2fr" : ""}`,
              textAlign: colSpan < 2 ? "right" : "left",
            } : {
              gap: 0.5,
              gridTemplateColumns: `1fr 3fr ${colSpan > 1 ? "1fr" : ""}`
            }}
          >
            {items.map(({ key, resource, control }) => {
              return (
                <div style={{ display: "contents" }} key={key}>
                  {canEdit && (
                    <PermissionToggle
                      checked={!resource.isDisabled}
                      onChange={() => onToggle(resource)}
                      color={getResourceBaseColor(resource) as SwitchProps["color"]}
                    />
                  )}
                  {!canEdit && (resource.isDisabled ? <CloseIcon /> : <CheckIcon />)}
                  <div style={{ fontWeight: 500 }}>{config.labelForKey(key)}</div>
                  {control && (
                    <>
                      {canEdit && (
                        <SmallSelect
                          size="small"
                          disabled={!canEdit || resource.isDisabled}
                          sx={selectSx}
                          value={control.value}
                          onChange={(e) =>
                            onApplyMeta(resource, control, String(e.target.value))
                          }
                        >
                          {control.options.map((k) => (
                            <MenuItem key={k} value={k}>
                              {k}
                            </MenuItem>
                          ))}
                        </SmallSelect>
                      )}
                      {!canEdit && control.value}
                    </>
                  )}
                </div>
              );
            })}
          </GroupedPermissionsContainer>
        </GroupedCardActions>
      </Center>
    </PackedCard>
  );
}
